import { useTranslation } from 'react-i18next';
import { Box, Stack } from '@mui/material';

import { CHANNEL_SHORT_NAME_MAP, TChannelProtocol } from '@/entities/channel';
import { getBillingDisplayPrices } from '@/features/billing/model/partnerDiscount';
import { PaymentMethodContent } from '@/features/billing/ui/paymentMethodContent/PaymentMethodContent';
import { ITariffGridDto, ITariffPlanDto } from '@/shared/api/subscriptions';
import { CustomTypography } from '@/shared/ui/Typography';

import {
  MAP_PAYMENTS_METHODS_TYPE_TO_I18_KEY,
  PARTNER_PAYMENTS_METHODS,
  PAYMENTS_METHODS_ICON,
  PAYMENTS_METHODS_MOCK,
} from '../model/constants';

import { CardItem } from './selectableCardGroup';
import {
  BoldSpan,
  PaymentInfoContainer,
  StyledDivider,
  StyledEndDivider,
  StyledSelectableCardGroup,
} from './styles';

interface IPaymentStepProps {
  channelProtocol?: TChannelProtocol;
  selectedTariff?: ITariffGridDto;
  selectedPlan?: ITariffPlanDto;
  selectedPaymentMethodId: number;
  onPaymentMethodChange: (id: number) => void;
  isPartnerPayment?: boolean;
  partnerDiscountRate?: number;
}

export const PaymentOrder = ({
  channelProtocol,
  selectedTariff,
  selectedPlan,
  selectedPaymentMethodId,
  onPaymentMethodChange,
  isPartnerPayment,
  partnerDiscountRate,
}: IPaymentStepProps) => {
  const { t } = useTranslation();
  const paymentMethods = isPartnerPayment
    ? PARTNER_PAYMENTS_METHODS
    : PAYMENTS_METHODS_MOCK;
  const { totalPrice, originalPrice } = getBillingDisplayPrices(
    selectedPlan?.final_price ?? 0,
    Boolean(isPartnerPayment),
    partnerDiscountRate
  );

  return (
    <Box sx={{ minWidth: '900px' }}>
      <PaymentInfoContainer>
        <Stack flexDirection="row" justifyContent="space-between">
          <CustomTypography>
            {t('billing:payment.subscription')}{' '}
            {channelProtocol && (
              <BoldSpan>{CHANNEL_SHORT_NAME_MAP[channelProtocol]}</BoldSpan>
            )}{' '}
            <BoldSpan>{selectedTariff?.tariff_info.name}</BoldSpan>{' '}
            {t('billing:payment.for')}{' '}
            <BoldSpan>{selectedPlan?.interval}</BoldSpan>{' '}
            <BoldSpan>
              {t('billing:month', {
                count: selectedPlan?.interval,
              })}
            </BoldSpan>
          </CustomTypography>
        </Stack>
        <StyledDivider />
        <Stack flexDirection="row" justifyContent="space-between">
          <CustomTypography sx={{ fontWeight: 'bold' }}>
            {t('billing:payment.total')}
          </CustomTypography>
          <Stack direction="row" alignItems="baseline" gap={1}>
            {originalPrice !== undefined && (
              <CustomTypography
                sx={{
                  color: 'text.secondary',
                  fontWeight: 400,
                  textDecoration: 'line-through',
                }}
              >
                {originalPrice.toLocaleString()} ₽
              </CustomTypography>
            )}
            <CustomTypography sx={{ fontWeight: 'bold' }}>
              {totalPrice.toLocaleString()} ₽
            </CustomTypography>
          </Stack>
        </Stack>
      </PaymentInfoContainer>
      <StyledSelectableCardGroup
        title={t('billing:payment.methods')}
        sx={{ maxWidth: '330px' }}
      >
        {paymentMethods.map((method) => {
          return (
            <CardItem
              key={method.id}
              selected={selectedPaymentMethodId === method.id}
              onClick={() => onPaymentMethodChange(method.id)}
            >
              <PaymentMethodContent
                selected={selectedPaymentMethodId === method.id}
                iconName={PAYMENTS_METHODS_ICON[method.type]}
                label={t(MAP_PAYMENTS_METHODS_TYPE_TO_I18_KEY[method.type])}
              />
            </CardItem>
          );
        })}
      </StyledSelectableCardGroup>
      <StyledEndDivider />
    </Box>
  );
};
